/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

/* eslint-disable max-len */

// pcg-viewer canvas: CWT scalogram renderer (core-viewer.js 2261-2415).
// Shim-prologue discipline as in draw-wave.js. Two deliberate edits beyond the
// canvas id lookup:
//   1. The prototype cached per-bin frequencies in a module global (_binFreqs) —
//      wrong once payloads can change; the cache now lives on rc.cache.
//   2. document.getElementById('cwtTitle').textContent becomes the optional
//      rc.chrome.setCwtTitle callback (React owns that label).

import { rejectGate, isGateHardRejected } from "../core/gate"
import { visualGuidePeaks as coreVisualGuidePeaks } from "../core/cycles"
import { tToX as viewTToX, CWT_BANDS } from "./view"
import { sizeCanvas } from "./draw-common"
import { evaluationGuideRange, outsideEvaluationRange } from "../core/evaluation-range"

// Prototype script lines 2262–2269 (see canvas/CANVAS-MAP.md) — cache moved from module global to rc.cache.
export function binFreqs(rc) {
  if (rc.cache._binFreqs) return rc.cache._binFreqs
  const D = rc.payload
  // The default guide payload carries no scalogram (`scalo` absent or `n_freq: 0`).
  const scalo = D.scalo || {}
  const nF = scalo.n_freq || 0,
    fMin = scalo.f_min,
    fMax = scalo.f_max
  const out = new Float32Array(nF)
  for (let f = 0; f < nF; f++) out[f] = fMin * Math.pow(fMax / fMin, f / (nF - 1))
  rc.cache._binFreqs = out
  return out
}

// core-viewer.js 2270-2278 — verbatim with (D, state) from rc.
export function selectedCwtBand(rc) {
  const D = rc.payload
  const scalo = D.scalo || {}
  const generated = { min: scalo.f_min || 20, max: scalo.f_max || 300 }
  const band = CWT_BANDS[rc.state.cwtBand] || CWT_BANDS.full
  return {
    label: band.label,
    min: Math.max(generated.min, band.min),
    max: Math.min(generated.max, band.max),
  }
}

export function drawSpec(rc) {
  const canvas = rc.canvases.spec
  if (!canvas) return
  // ---- shim prologue (globals -> render context) ----
  const D = rc.payload
  const SCALO = rc.traces.scalo
  const { view, state } = rc
  const visualGuidePeaks = () => coreVisualGuidePeaks(D, rc.core.ctx.phaseReviewResolution)
  const tToX = (t, pL, pW) => viewTToX(view, t, pL, pW)
  // Timing engine (upstream v0.9.1): guides outside the Evaluation Range are dimmed (see draw-wave.js).
  const mvp2OutsideEvalGuideContext = (t) =>
    outsideEvaluationRange(evaluationGuideRange(rc.core.ctx.evaluationRange), t)
  // ---- end shim prologue; body verbatim below ----
  const { ctx, W, H } = sizeCanvas(canvas, 220)
  const pL = 46,
    pR = 10,
    pT = 10,
    pB = 26,
    pW = W - pL - pR,
    pH = H - pT - pB
  const scalo = D.scalo || {}
  const nF = scalo.n_freq,
    nT = scalo.n_time
  const gateRejected = isGateHardRejected(rejectGate(D))
  if (!nF || !nT || SCALO.length < nF * nT) {
    ctx.clearRect(0, 0, W, H)
    ctx.fillStyle = "#111827"
    ctx.fillRect(pL, pT, pW, pH)
    ctx.fillStyle = "#6b7280"
    ctx.font = "11px sans-serif"
    ctx.textAlign = "center"
    ctx.fillText("CWT guide not generated for browser upload", pL + pW / 2, pT + pH / 2 - 6)
    ctx.fillText(
      "Waveform, playback, S1/S2, Sys/Dia, and energy guides remain available",
      pL + pW / 2,
      pT + pH / 2 + 12
    )
    return
  }
  const cellTime = D.duration / nT
  const t0 = Math.max(0, Math.floor(view.start / cellTime) - 1)
  const t1 = Math.min(nT, Math.ceil((view.start + view.width) / cellTime) + 1)
  const cH = pH / nF
  const freqs = binFreqs(rc)
  const murmurOn = !gateRejected && state.layers.murmur
  const visibleBand = selectedCwtBand(rc)
  if (rc.chrome.setCwtTitle) rc.chrome.setCwtTitle(`CWT — ${visibleBand.label}`)

  // Heatmap cells.  When Energy is OFF, cells in the 100-300 Hz band are
  // drained to a desaturated grey so they don't visually pull the eye —
  // the data is still discoverable but the turbulent-energy band isn't foregrounded
  // unless the doctor opts in.
  for (let t = t0; t < t1; t++) {
    const x0 = tToX(t * cellTime, pL, pW)
    const x1 = tToX((t + 1) * cellTime, pL, pW)
    const cW = x1 - x0 + 0.5
    for (let f = 0; f < nF; f++) {
      const v = SCALO[f * nT + t]
      const inVisibleBand = freqs[f] >= visibleBand.min && freqs[f] <= visibleBand.max
      const inMurmur = freqs[f] >= 100
      let r, g, b
      if (!inVisibleBand) {
        const s = Math.min(v, 1)
        r = 11 + ((s * 22) | 0)
        g = 13 + ((s * 24) | 0)
        b = 18 + ((s * 30) | 0)
      } else if (inMurmur && !murmurOn) {
        // Desaturate: collapse to a dim grey ramp
        const s = Math.min(v, 1)
        r = 20 + ((s * 80) | 0)
        g = 22 + ((s * 84) | 0)
        b = 28 + ((s * 92) | 0)
      } else if (v < 0.25) {
        const s = v / 0.25
        r = (10 + s * 20) | 0
        g = (10 + s * 40) | 0
        b = (30 + s * 100) | 0
      } else if (v < 0.5) {
        const s = (v - 0.25) / 0.25
        r = (30 + s * 10) | 0
        g = (50 + s * 150) | 0
        b = (130 + s * 50) | 0
      } else if (v < 0.75) {
        const s = (v - 0.5) / 0.25
        r = (40 + s * 200) | 0
        g = (200 - s * 50) | 0
        b = (180 - s * 150) | 0
      } else {
        const s = (v - 0.75) / 0.25
        r = (240 + s * 15) | 0
        g = (150 - s * 100) | 0
        b = (30 - s * 20) | 0
      }
      ctx.fillStyle = `rgb(${r},${g},${b})`
      ctx.fillRect(x0, pT + pH - (f + 1) * cH, cW, cH + 0.5)
    }
  }

  // Sys/Dia overlay (thin top/bottom bands)
  if (!gateRejected && state.layers.regions) {
    const peaks = visualGuidePeaks()
    const tEnd = view.start + view.width
    for (let i = 0; i < peaks.length - 1; i++) {
      const a = peaks[i],
        b = peaks[i + 1]
      if (b.t < view.start || a.t > tEnd) continue
      const outsideEvalContext = mvp2OutsideEvalGuideContext((a.t + b.t) / 2)
      const x1 = tToX(a.t, pL, pW),
        x2 = tToX(b.t, pL, pW)
      const w = x2 - x1
      if (a.l === "S1" && b.l === "S2") {
        ctx.fillStyle = outsideEvalContext ? "rgba(148,163,184,0.15)" : "rgba(148,163,184,0.45)"
        ctx.fillRect(x1, pT, w, 3)
        ctx.fillRect(x1, pT + pH - 3, w, 3)
      } else if (a.l === "S2" && b.l === "S1") {
        ctx.fillStyle = outsideEvalContext ? "rgba(241,245,249,0.07)" : "rgba(241,245,249,0.20)"
        ctx.fillRect(x1, pT, w, 3)
        ctx.fillRect(x1, pT + pH - 3, w, 3)
      }
    }
  }

  // Frequency reference lines — each tied to its owning Feature toggle.
  const bandLines = []
  if (!gateRejected && state.layers.s1s2) {
    bandLines.push({ f: 80, c: "rgba(203,213,225,0.55)", label: "80 (S1/S2)" })
  }
  if (!gateRejected && state.layers.murmur) {
    bandLines.push({ f: 35, c: "rgba(248,113,113,0.42)", label: "35" })
    bandLines.push({ f: 150, c: "rgba(248,113,113,0.55)", label: "150 (energy)" })
    bandLines.push({ f: 250, c: "rgba(239,68,68,0.55)", label: "250" })
  }
  bandLines.forEach((b) => {
    if (b.f < scalo.f_min || b.f > scalo.f_max) return
    const yFrac = (Math.log10(b.f) - Math.log10(scalo.f_min)) / (Math.log10(scalo.f_max) - Math.log10(scalo.f_min))
    const y = pT + pH - yFrac * pH
    ctx.strokeStyle = b.c
    ctx.lineWidth = 1
    ctx.setLineDash([4, 4])
    ctx.beginPath()
    ctx.moveTo(pL, y)
    ctx.lineTo(pL + pW, y)
    ctx.stroke()
    ctx.setLineDash([])
    ctx.fillStyle = b.c.replace(/[\d.]+\)$/, "0.95)")
    ctx.font = "8px sans-serif"
    ctx.textAlign = "right"
    ctx.fillText(b.label, pL + pW - 3, y - 2)
  })

  if (!gateRejected && state.layers.s1s2) {
    const tEnd = view.start + view.width
    visualGuidePeaks().forEach((p) => {
      if (p.t < view.start || p.t > tEnd) return
      const outsideEvalContext = mvp2OutsideEvalGuideContext(p.t)
      const x = tToX(p.t, pL, pW)
      const isInferred = p.source === "inferred" || p.promoted === true
      ctx.strokeStyle = isInferred
        ? outsideEvalContext
          ? "rgba(148,163,184,0.14)"
          : "rgba(148,163,184,0.38)"
        : outsideEvalContext
        ? p.l === "S1"
          ? "rgba(148,163,184,0.22)"
          : "rgba(100,116,139,0.18)"
        : p.l === "S1"
        ? "rgba(148,163,184,0.75)"
        : "rgba(100,116,139,0.65)"
      ctx.lineWidth = 1
      ctx.setLineDash(isInferred ? [1, 5] : [2, 2])
      ctx.beginPath()
      ctx.moveTo(x, pT)
      ctx.lineTo(x, pT + pH)
      ctx.stroke()
      ctx.setLineDash([])
    })
  }

  ctx.fillStyle = "#4b5563"
  ctx.font = "9px sans-serif"
  ctx.textAlign = "right"
  ;[20, 35, 50, 100, 150, 200, 250, 300].forEach((f) => {
    if (f < scalo.f_min || f > scalo.f_max) return
    const yFrac = (Math.log10(f) - Math.log10(scalo.f_min)) / (Math.log10(scalo.f_max) - Math.log10(scalo.f_min))
    const y = pT + pH - yFrac * pH
    ctx.fillText(f + "Hz", pL - 5, y + 3)
  })
  let tStep
  if (view.width > 12) tStep = 2
  else if (view.width > 6) tStep = 1
  else if (view.width > 3) tStep = 0.5
  else if (view.width > 1.5) tStep = 0.25
  else tStep = 0.1
  const tickStart = Math.ceil(view.start / tStep) * tStep
  for (let t = tickStart; t <= view.start + view.width + 1e-6; t += tStep) {
    ctx.fillStyle = "#4b5563"
    ctx.font = "10px sans-serif"
    ctx.textAlign = "center"
    ctx.fillText(t.toFixed(tStep < 0.5 ? 2 : 1) + "s", tToX(t, pL, pW), H - 8)
  }
}
